import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankStatementColumnMapping, BankStatementImportCommitRequest, BankStatementImportPreview } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-statement-import',
    standalone: true,
    providers: [MessageService],
    imports: [CommonModule, FormsModule, ButtonModule, DatePickerModule, InputNumberModule, InputTextModule, SelectModule, TableModule,
        TagModule, ToastModule, PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <p-toast></p-toast>
        <app-page-shell>
            <app-page-hero eyebrow="Statement Import" title="Import Bank Statement" subtitle="Upload CSV/XLSX, validate rows, map bank columns, and commit only clean non-duplicate lines.">
                <div actions>
                    <button pButton type="button" icon="pi pi-list" label="Statements" [outlined]="true" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/statements'])"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-data-panel title="1. Upload" subtitle="Choose a company bank account and upload the original bank file.">
                <div class="grid-form">
                    <label>Bank account
                        <p-select [options]="bankAccounts" [(ngModel)]="bankAccountId" optionLabel="accountName" optionValue="id" placeholder="Select account"></p-select>
                    </label>
                    <label>Statement file
                        <input type="file" accept=".csv,.xls,.xlsx" (change)="onFileSelected($event)">
                    </label>
                    <div class="form-actions">
                        <button pButton type="button" icon="pi pi-search" label="Preview" [disabled]="!bankAccountId || !selectedFile || loading" (click)="preview()"></button>
                    </div>
                </div>
            </app-page-data-panel>

            @if (previewResult) {
                <app-page-data-panel title="2. Column Mapping" subtitle="Auto-detected headers can be overridden and previewed again.">
                    <div class="mapping-grid">
                        <label>Transaction date <p-select [options]="headerOptions" [(ngModel)]="mapping.transactionDate" [showClear]="true"></p-select></label>
                        <label>Value date <p-select [options]="headerOptions" [(ngModel)]="mapping.valueDate" [showClear]="true"></p-select></label>
                        <label>Description <p-select [options]="headerOptions" [(ngModel)]="mapping.description" [showClear]="true"></p-select></label>
                        <label>Bank reference <p-select [options]="headerOptions" [(ngModel)]="mapping.bankReference" [showClear]="true"></p-select></label>
                        <label>Debit <p-select [options]="headerOptions" [(ngModel)]="mapping.debit" [showClear]="true"></p-select></label>
                        <label>Credit <p-select [options]="headerOptions" [(ngModel)]="mapping.credit" [showClear]="true"></p-select></label>
                        <label>Signed amount <p-select [options]="headerOptions" [(ngModel)]="mapping.amount" [showClear]="true"></p-select></label>
                        <label>Running balance <p-select [options]="headerOptions" [(ngModel)]="mapping.runningBalance" [showClear]="true"></p-select></label>
                        <label>Counterparty <p-select [options]="headerOptions" [(ngModel)]="mapping.counterparty" [showClear]="true"></p-select></label>
                    </div>
                    <div class="form-actions">
                        <button pButton type="button" icon="pi pi-refresh" label="Re-preview With Mapping" [outlined]="true" [disabled]="loading" (click)="preview()"></button>
                    </div>
                    @if (previewResult.mappingWarnings.length) {
                        <div class="warning-list">
                            @for (warning of previewResult.mappingWarnings; track warning) { <span>{{ warning }}</span> }
                        </div>
                    }
                </app-page-data-panel>

                <app-page-data-panel title="3. Preview Rows" [resultText]="previewResult.validRows + ' valid / ' + previewResult.duplicateRows + ' duplicates / ' + previewResult.errorRows + ' errors'">
                    <p-table [value]="previewResult.lines" styleClass="p-datatable-sm" [paginator]="true" [rows]="15">
                        <ng-template pTemplate="header">
                            <tr>
                                <th>#</th><th>Date</th><th>Reference</th><th>Description</th><th>Debit</th><th>Credit</th><th>Status</th>
                            </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-line>
                            <tr>
                                <td>{{ line.lineNumber }}</td>
                                <td>{{ line.transactionDate || '-' }}</td>
                                <td>{{ line.bankReference || line.customerReference || '-' }}</td>
                                <td>{{ line.description || '-' }}</td>
                                <td>{{ line.debit | number:'1.2-2' }}</td>
                                <td>{{ line.credit | number:'1.2-2' }}</td>
                                <td>
                                    <p-tag [value]="line.error ? line.error : (line.duplicate ? 'Duplicate' : 'Valid')" [severity]="line.error ? 'danger' : (line.duplicate ? 'warn' : 'success')"></p-tag>
                                </td>
                            </tr>
                        </ng-template>
                    </p-table>
                </app-page-data-panel>

                <app-page-data-panel title="4. Statement Metadata" subtitle="Commit creates the statement and imports valid non-duplicate lines.">
                    <div class="grid-form">
                        <label>Statement number <input pInputText [(ngModel)]="commitRequest.statementNumber"></label>
                        <label>Currency <input pInputText maxlength="3" [(ngModel)]="commitRequest.currencyCode"></label>
                        <label>Period start <p-datePicker [(ngModel)]="periodStartDate" dateFormat="yy-mm-dd"></p-datePicker></label>
                        <label>Period end <p-datePicker [(ngModel)]="periodEndDate" dateFormat="yy-mm-dd"></p-datePicker></label>
                        <label>Opening balance <p-inputNumber [(ngModel)]="commitRequest.openingBalance" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber></label>
                        <label>Closing balance <p-inputNumber [(ngModel)]="commitRequest.closingBalance" mode="decimal" [minFractionDigits]="2" [maxFractionDigits]="4"></p-inputNumber></label>
                    </div>
                    <div class="form-actions">
                        <button pButton type="button" icon="pi pi-check" label="Commit Import" [disabled]="!previewResult.importId || previewResult.validRows === 0 || loading" (click)="commit()"></button>
                    </div>
                </app-page-data-panel>
            }
        </app-page-shell>
    `,
    styles: [`
        .grid-form, .mapping-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: .85rem; }
        label { display: flex; flex-direction: column; gap: .35rem; color: #334155; font-weight: 700; }
        input[type="file"], input[pInputText] { width: 100%; border: 1px solid #dfe7f3; border-radius: 6px; padding: .65rem; }
        .form-actions { display: flex; align-items: end; gap: .5rem; margin-top: .75rem; }
        .warning-list { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .75rem; color: #92400e; }
        .warning-list span { background: #fef3c7; border-radius: 6px; padding: .45rem .6rem; }
    `]
})
export class BankStatementImportComponent implements OnInit {
    bankAccounts: BankAccount[] = [];
    bankAccountId?: number;
    selectedFile?: File;
    previewResult?: BankStatementImportPreview;
    mapping: BankStatementColumnMapping = {};
    commitRequest: BankStatementImportCommitRequest = { currencyCode: 'SAR' };
    periodStartDate?: Date;
    periodEndDate?: Date;
    loading = false;
    metrics: PageMetric[] = [
        { label: 'Rows', value: '-', tone: 'info' },
        { label: 'Valid', value: '-', tone: 'success' },
        { label: 'Duplicates', value: '-', tone: 'warn' },
        { label: 'Errors', value: '-', tone: 'danger' }
    ];

    constructor(
        private bankAccountService: BankAccountService,
        private bankReconciliationService: BankReconciliationService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        public router: Router
    ) {}

    get headerOptions(): { label: string; value: string }[] {
        return (this.previewResult?.headers ?? []).map(header => ({ label: header, value: header }));
    }

    ngOnInit(): void {
        this.bankAccountService.getBankAccounts('Company', this.companyId(), '', 0, 100).subscribe(result => this.bankAccounts = result.items ?? []);
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectedFile = input.files?.[0];
    }

    preview(): void {
        if (!this.bankAccountId || !this.selectedFile) return;
        this.loading = true;
        this.bankReconciliationService.previewImport(this.companyId(), this.bankAccountId, this.selectedFile, this.mapping).subscribe({
            next: result => {
                this.previewResult = result;
                this.metrics = [
                    { label: 'Rows', value: result.totalRows, tone: 'info' },
                    { label: 'Valid', value: result.validRows, tone: 'success' },
                    { label: 'Duplicates', value: result.duplicateRows, tone: 'warn' },
                    { label: 'Errors', value: result.errorRows, tone: 'danger' }
                ];
                this.loading = false;
            },
            error: error => {
                this.messageService.add({ severity: 'error', summary: 'Preview failed', detail: error?.error?.message ?? 'Unable to preview statement.' });
                this.loading = false;
            }
        });
    }

    commit(): void {
        if (!this.previewResult?.importId) return;
        this.loading = true;
        const request: BankStatementImportCommitRequest = {
            ...this.commitRequest,
            periodStartDate: this.toDate(this.periodStartDate),
            periodEndDate: this.toDate(this.periodEndDate),
            importedBy: this.sessionStorageService.getCustomer()?.id
        };
        this.bankReconciliationService.commitImport(this.previewResult.importId, request).subscribe({
            next: result => this.router.navigate(['/accounts/enterprise/bank-reconciliation/statements', result.statement.statementId]),
            error: error => {
                this.messageService.add({ severity: 'error', summary: 'Commit failed', detail: error?.error?.message ?? 'Unable to commit import.' });
                this.loading = false;
            }
        });
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }

    private toDate(value?: Date): string | undefined {
        return value ? value.toISOString().slice(0, 10) : undefined;
    }
}
